import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, User, Shield, Check, X, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ApiService from '../services/api';

const emptyForm = {
  username: '',
  email: '',
  password: '',
  name: '',
  role: 'user'
};

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ApiService.getUsers();
      setUsers(res.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin');
      return;
    }
    loadUsers();
  }, [isAdmin, navigate, loadUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      if (editingUser) {
        await ApiService.updateUser(editingUser.id, {
          fullName: formData.name,
          email: formData.email,
          role: formData.role,
          password: formData.password || undefined
        });
        setMessage('User updated');
      } else {
        await ApiService.createUser({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          fullName: formData.name,
          role: formData.role
        });
        setMessage('User created');
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData(emptyForm);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      name: user.full_name || user.name || '',
      role: user.role
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      alert('You cannot delete your own account');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await ApiService.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await ApiService.approveContributor(userId);
      setMessage('Contributor approved and notified by email');
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Approve failed');
    }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt('Optional rejection reason (emailed to the applicant):', '') || '';
    try {
      await ApiService.rejectContributor(userId, reason);
      setMessage('Application rejected; user kept as regular member');
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Reject failed');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const pending = users.filter((u) => u.role === 'author' && u.status === 'pending');

  const roleBadge = (user) => {
    const isPending = user.role === 'author' && user.status === 'pending';
    const bg = user.role === 'admin' ? '#e8f5e8' : isPending ? '#fff7ed' : user.role === 'author' ? '#ecfeff' : '#f3f4f6';
    const color = user.role === 'admin' ? '#166534' : isPending ? '#9a3412' : user.role === 'author' ? '#155e75' : '#374151';
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 8px',
        borderRadius: '12px',
        background: bg,
        color,
        fontSize: '0.85rem'
      }}>
        {user.role === 'admin' ? <Shield size={14} /> : isPending ? <Clock size={14} /> : <User size={14} />}
        {user.role}{isPending ? ' · pending' : ''}
      </span>
    );
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1 className="page-title">User Management</h1>
        </div>

        <button onClick={() => { setShowForm(true); setEditingUser(null); setFormData(emptyForm); }} className="btn btn-primary">
          <Plus size={16} />
          Add New User
        </button>
      </div>

      {error && <div className="auth-alert auth-alert-error" style={{ marginBottom: '16px' }}>{error}</div>}
      {message && <div className="auth-alert auth-alert-info" style={{ marginBottom: '16px' }}>{message}</div>}

      {pending.length > 0 && (
        <div className="data-table" style={{ marginBottom: '24px' }}>
          <h3 style={{ padding: '20px', margin: 0, background: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
            Pending contributor applications ({pending.length})
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Experience</th>
                <th>Applied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.full_name || user.name}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>@{user.username}</div>
                    {user.job_title && <div style={{ fontSize: '0.85rem' }}>{user.job_title}</div>}
                  </td>
                  <td>{user.email}</td>
                  <td style={{ maxWidth: '280px' }}>
                    <div style={{ fontSize: '0.9rem' }}>{user.professional_experience || '—'}</div>
                    {user.linkedin_url && (
                      <a href={user.linkedin_url} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                    )}
                  </td>
                  <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                  <td>
                    <button onClick={() => handleApprove(user.id)} className="btn btn-primary" style={{ marginRight: '8px' }}>
                      <Check size={14} /> Approve
                    </button>
                    <button onClick={() => handleReject(user.id)} className="btn btn-danger">
                      <X size={14} /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px' }}>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  name="role"
                  className="form-input"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="user">Regular user</option>
                  <option value="author">Contributor (author)</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{editingUser ? 'New password (optional)' : 'Password'}</label>
              <input
                type="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
                minLength={editingUser ? undefined : 6}
              />
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowForm(false); setEditingUser(null); }} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Update User' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="data-table">
        <h3 style={{ padding: '20px', margin: 0, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
          All Users {loading ? '(loading…)' : `(${users.length})`}
        </h3>
        <table className="table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.full_name || user.name}</td>
                <td>{user.email}</td>
                <td>{roleBadge(user)}</td>
                <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <button onClick={() => handleEdit(user)} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                    <Edit size={14} />
                    Edit
                  </button>
                  {user.id !== currentUser.id && (
                    <button onClick={() => handleDelete(user.id)} className="btn btn-danger">
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
